/**
 * Reads chart chrome from the same CSS variables the app is themed with, so
 * Recharts follows a theme swap without a second palette to maintain.
 *
 * Call inside a component that re-renders on theme change (subscribe to
 * useUiStore) — the values are resolved at call time, not cached.
 */
export const chartTheme = () => {
  const css = getComputedStyle(document.documentElement)
  const token = (name: string) => css.getPropertyValue(name).trim()

  return {
    grid: token('--chart-grid'),
    axis: token('--chart-axis'),
    up: token('--market-up'),
    down: token('--market-down'),
    flat: token('--market-flat'),
    /** A single-series bar that isn't up or down — see --chart-accent. */
    accent: token('--chart-accent'),
    /** The last point on a line and the hover point — IndexChart and
     *  Sparkline both read this one gold token rather than the direction
     *  colour, so a dot never doubles as a second "up"/"down" signal. */
    marker: token('--chart-marker'),
    border: token('--border'),
    card: token('--card'),
    foreground: token('--foreground'),
    /**
     * Categorical series, for allocation and sector breakdowns.
     *
     * These read Layer 2, not the raw ramp. Naming primitives here was what
     * broke when the placeholder palette was swapped for the real design
     * system: the old --a-600 / --n-500 names stopped existing and
     * getPropertyValue returned '' , so every slice rendered black.
     */
    categorical: [
      token('--chart-cat-1'),
      token('--chart-cat-2'),
      token('--chart-cat-3'),
      token('--chart-cat-4'),
      token('--chart-cat-5'),
      token('--chart-cat-6'),
    ],
    /**
     * A second ramp for breakdowns `categorical` cannot serve — Sectors'
     * turnover ring (`4236:108727`) opens on green where dark `categorical`
     * has no green until its last slot (the `mk:sectors` block in
     * tokens.semantic.css sets the five the mock draws). A SEPARATE token block rather than widening
     * `--chart-cat-*` itself: Portfolio's `BySectorCard` reads `categorical`
     * through `seriesColor` too, and must not recolour just because Sectors
     * needed more room.
     */
    turnover: [
      token('--chart-turnover-1'),
      token('--chart-turnover-2'),
      token('--chart-turnover-3'),
      token('--chart-turnover-4'),
      token('--chart-turnover-5'),
      token('--chart-turnover-6'),
      token('--chart-turnover-7'),
    ],
  }
}

export type ChartTheme = ReturnType<typeof chartTheme>

/**
 * The type scale's two smallest steps, for SVG attributes, which take a number
 * where a `text-*` utility cannot reach: 12 for axis ticks and figures on a
 * mark (`text-body-xs`), 14 for a figure read as a headline (`text-h-s`). The
 * scale has nothing smaller than 12.
 */
export const CHART_TEXT_XS = 12
export const CHART_TEXT_S = 14

/**
 * Colour for the nth categorical series, wrapping round the palette.
 *
 * Indexing an array yields `string | undefined` under
 * noUncheckedIndexedAccess, and Recharts types `fill` as a required `string`
 * under exactOptionalPropertyTypes. The modulo keeps the index in range but
 * the compiler cannot see that, so the narrowing happens here once rather
 * than as a non-null assertion at every call site — strictTypeChecked bans
 * those, and an empty palette would then render a chart with no fill at all
 * instead of falling back to a visible colour.
 */
export const seriesColor = (theme: ChartTheme, index: number): string =>
  theme.categorical[index % theme.categorical.length] ?? theme.foreground

/** Same contract as `seriesColor`, over the 7-hue `turnover` ramp. */
export const turnoverSeriesColor = (theme: ChartTheme, index: number): string =>
  theme.turnover[index % theme.turnover.length] ?? theme.foreground

export type MarketDirection = 'up' | 'down' | 'flat'

/**
 * Line and area-fill pair for a directional series.
 *
 * The line is the FILL grade (`--market-up-fill` / `--market-down-fill`), not
 * `--market-up`/`--market-down`: those are tuned a shade lighter for text on
 * the card, and a canvas stroke is drawn at the same weight the 2026-09-28
 * design refresh sizes candles and bars at.
 *
 * The fill is the `-soft` token, not the line colour at reduced alpha: an
 * rgba() literal is a build error under the token rule, and the soft tokens
 * are already the tint the design system tuned against each canvas. Fading it
 * to `transparent` keeps the gradient inside the palette.
 */
export const marketPalette = (direction: MarketDirection) => {
  const css = getComputedStyle(document.documentElement)
  const token = (name: string) => css.getPropertyValue(name).trim()

  if (direction === 'up')
    return { line: token('--market-up-fill'), fill: token('--market-up-soft') }
  if (direction === 'down')
    return { line: token('--market-down-fill'), fill: token('--market-down-soft') }
  return { line: token('--market-flat'), fill: token('--border-subtle') }
}

/**
 * One Layer-2 token, resolved now.
 *
 * The hand-rolled SVG primitives need a colour as a real string — an SVG
 * `fill` cannot be a Tailwind utility on a shape that also has to be measured
 * — but they only need one or two tokens, not the whole chrome map. Resolved
 * at call time like everything else here, so a component that re-renders on a
 * theme change picks up the new palette.
 */
export const cssToken = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim()

/**
 * Prices inside a chart, where the value is a plain number rather than Money.
 *
 * lib/format's helpers all take the branded Money string; a chart axis or an
 * SVG label has already crossed into float territory by the time it is drawn
 * (see the money-rule exemption for this directory), so the grouping is
 * repeated here rather than round-tripping a float back through `money()` and
 * pretending it is exact.
 */
const CHART_PRICE = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const formatChartPrice = (value: number): string =>
  Number.isFinite(value) ? CHART_PRICE.format(value) : '—'
